import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { VideoModelsCard } from "./VideoModelsCard";
import { MaestroStudioCard } from "./MaestroStudioCard";

/**
 * Which model does which job, and the place to change it.
 *
 * The list comes from the same registry the engine resolves against at run time,
 * so this page cannot claim one thing while production does another.
 *
 * Tasks are ordered by SPEND, not alphabetically. The article body is called once
 * per section and dominates the bill; changing "SEO title" saves nothing. Sorting
 * by spend puts the only decision that matters at the top.
 */

interface Assignment {
  id: string;
  label: string;
  what: string;
  envKey: string;
  group: "production" | "research" | "operations";
  spend: "high" | "medium" | "low";
  byPageType: boolean;
  override: string | null;
  overrideInvalid: boolean;
  effective: string;
  effectivePillar: string;
  defaultModel: string;
  priceInput: number | null;
  priceOutput: number | null;
}

interface ProbeResult {
  id: string;
  label: string;
  tier: string;
  sentAs: string;
  ok: boolean;
  error: string | null;
}

interface Probe {
  provider: string;
  region: string | null;
  results: ProbeResult[];
  brokenTasks: { task: string; label: string; pillarPage: boolean; model: string }[];
  checkedAt: string;
}

interface CatalogModel {
  id: string;
  label: string;
  tier: "current" | "legacy" | "deprecated";
  family: string;
  priceInput: number;
  priceOutput: number;
  contextWindow: number;
  description: string;
}

const GROUPS: { id: Assignment["group"]; title: string; blurb: string }[] = [
  { id: "production", title: "Content production", blurb: "Everything that turns a keyword into a published article." },
  { id: "research", title: "Research + discovery", blurb: "Finding and expanding what to write about." },
  { id: "operations", title: "Operations", blurb: "Tools you interact with directly." },
];

const SPEND_ORDER: Record<Assignment["spend"], number> = { high: 0, medium: 1, low: 2 };

function SpendPill({ spend }: { spend: Assignment["spend"] }) {
  const map = {
    high: "bg-rose-100 text-rose-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-slate-100 text-slate-600",
  } as const;
  const label = { high: "most spend", medium: "some spend", low: "minimal spend" } as const;
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${map[spend]}`}>{label[spend]}</span>;
}

export function ModelsPage() {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<{
    provider: string;
    catalog: CatalogModel[];
    assignments: Assignment[];
  }>({
    queryKey: ["models"],
    queryFn: async () => {
      const r = await fetch("/api/models", { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });

  const save = useMutation({
    mutationFn: async ({ id, model }: { id: string; model: string | null }) => {
      const r = await fetch(`/api/models/${encodeURIComponent(id)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ model }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.error ?? `HTTP ${r.status}`);
      return { id };
    },
    onMutate: () => setErr(null),
    onSuccess: ({ id }) => {
      setSavedId(id);
      window.setTimeout(() => setSavedId((c) => (c === id ? null : c)), 2000);
      void qc.invalidateQueries({ queryKey: ["models"] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  const probe = useMutation({
    mutationFn: async () => {
      const r = await fetch("/api/models/probe", { method: "POST", credentials: "include" });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.error ?? `HTTP ${r.status}`);
      return body as Probe;
    },
    onMutate: () => setErr(null),
    onError: (e: Error) => setErr(e.message),
  });

  if (isLoading) return <div className="p-6 text-sm text-slate-500">Loading model assignments…</div>;
  if (error) return <div className="p-6 text-sm text-red-600">Could not load models: {String(error)}</div>;

  const catalog = data?.catalog ?? [];
  const assignments = data?.assignments ?? [];

  return (
    <div className="space-y-8 p-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Models</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          Which model does which job. Changes save immediately and apply to the next call — no restart. Leave a task
          on <span className="font-medium">Default</span> and it follows the engine default, so you only pin what you
          actually want to control.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Running through <span className="font-medium">{data?.provider}</span>. Tasks are ordered by how much of the
          bill they account for — the article body is called once per section and dominates it.
        </p>
        {err && (
          <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>
        )}

        {/* Listed in the catalog is NOT granted to the account. The only honest
            answer is a real invoke, which is what this runs. */}
        <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={probe.isPending}
              onClick={() => probe.mutate()}
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              {probe.isPending ? "Checking…" : "Check availability"}
            </button>
            <span className="text-xs text-slate-600">
              Invokes every model for real (1 token each) on <span className="font-medium">{data?.provider}</span>.
              A model can be listed in the catalog and still be denied to this account.
            </span>
          </div>

          {probe.data && (
            <div className="mt-3">
              {probe.data.brokenTasks.length > 0 ? (
                <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800">
                  <div className="font-medium">
                    {probe.data.brokenTasks.length} task(s) point at a model this account cannot invoke:
                  </div>
                  <ul className="mt-1 space-y-0.5">
                    {probe.data.brokenTasks.map((b, i) => (
                      <li key={i}>
                        {b.label}
                        {b.pillarPage ? " (pillar pages)" : ""} → <code>{b.model}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="rounded border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-800">
                  Every task resolves to a model this account can invoke.
                </div>
              )}
              <div className="mt-2 grid gap-1 sm:grid-cols-2">
                {probe.data.results.map((r) => (
                  <div key={r.id} className="flex items-start gap-2 text-xs">
                    <span>{r.ok ? "✅" : "❌"}</span>
                    <span className="min-w-0">
                      <code className="text-slate-700">{r.id}</code>
                      {!r.ok && <span className="block text-[11px] text-slate-500">{r.error}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      {GROUPS.map((g) => {
        const items = assignments
          .filter((a) => a.group === g.id)
          .sort((a, b) => SPEND_ORDER[a.spend] - SPEND_ORDER[b.spend]);
        if (!items.length) return null;
        return (
          <section key={g.id}>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{g.title}</h2>
            <p className="mb-3 text-xs text-slate-500">{g.blurb}</p>
            <div className="space-y-3">
              {items.map((a) => (
                <div key={a.id} className="rounded-lg border border-slate-200 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-slate-900">{a.label}</span>
                        <SpendPill spend={a.spend} />
                        {savedId === a.id && <span className="text-[11px] text-emerald-600">saved</span>}
                      </div>
                      <p className="mt-1 max-w-2xl text-xs text-slate-600">{a.what}</p>
                      <code className="mt-1 block text-[11px] text-slate-400">{a.envKey}</code>
                    </div>

                    <div className="w-64 shrink-0">
                      <select
                        value={a.override ?? ""}
                        disabled={save.isPending}
                        onChange={(e) => save.mutate({ id: a.id, model: e.target.value || null })}
                        className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs disabled:bg-slate-50"
                      >
                        <option value="">
                          Default{a.byPageType ? " (by page type)" : ` — ${a.defaultModel}`}
                        </option>
                        {catalog.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.label} · ${m.priceInput}/${m.priceOutput} per 1M
                            {m.tier === "legacy" ? " · legacy" : ""}
                          </option>
                        ))}
                      </select>
                      <div className="mt-1 text-[11px] text-slate-500">
                        {a.byPageType && !a.override ? (
                          <>
                            Standard: <span className="font-medium">{a.effective}</span>
                            <br />
                            Pillar pages: <span className="font-medium">{a.effectivePillar}</span>
                          </>
                        ) : (
                          <>
                            Runs: <span className="font-medium">{a.effective}</span>
                            {a.priceInput !== null && (
                              <>
                                {" "}
                                · ${a.priceInput}/${a.priceOutput} per 1M
                              </>
                            )}
                          </>
                        )}
                      </div>
                      {a.overrideInvalid && (
                        <div className="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
                          <span className="font-medium">“{a.override}” is not a known model.</span> This task is
                          falling back to {a.defaultModel} at run time.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Available models</h2>
        <p className="mb-3 text-xs text-slate-500">Prices are per 1M tokens on the direct API; Bedrock is close but can differ.</p>
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Model</th>
                <th className="px-4 py-2 font-medium">Tier</th>
                <th className="px-4 py-2 font-medium">In / Out</th>
                <th className="px-4 py-2 font-medium">Context</th>
                <th className="px-4 py-2 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {catalog.map((m) => (
                <tr key={m.id}>
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-900">{m.label}</div>
                    <code className="text-[11px] text-slate-400">{m.id}</code>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">{m.tier}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-xs tabular-nums text-slate-600">
                    ${m.priceInput} / ${m.priceOutput}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-xs tabular-nums text-slate-600">
                    {(m.contextWindow / 1000).toLocaleString()}K
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">{m.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <VideoModelsCard />

      <MaestroStudioCard />
    </div>
  );
}
